import React from 'react'
import { Link, useNavigate } from 'react-router-dom'
import QuantityControl from '../helper/QuantityControl'
import { useGlobal } from '../context/GlobalContext'
import { formatImageUrl } from '../utils/formatImageUrl';
import toast from 'react-hot-toast';

const getPrice = (item) => {
    if (typeof item.price === 'number') return item.price;
    if (typeof item.currentPrice === 'number') return item.currentPrice;
    if (typeof item.currentPrice === 'string') return parseFloat(item.currentPrice.replace(/[^0-9.]/g, ''));
    if (typeof item.price === 'string') return parseFloat(item.price.replace(/[^0-9.]/g, ''));
    return 0;
}

const CartSection = () => {
    const navigate = useNavigate();
    const { cartItems, removeFromCart, updateCartQuantity } = useGlobal();

    const isLoggedIn = !!localStorage.getItem('token');

    const handleProceedToCheckout = () => {
        if (!isLoggedIn) {
            toast("Please login to complete your checkout.", { icon: "🔒" });
            navigate('/login?redirect=/checkout', { state: { from: '/checkout' } });
        } else {
            navigate('/checkout');
        }
    };

    const subtotal = cartItems.reduce((acc, item) => {
        return acc + (getPrice(item) * (item.quantity || 1));
    }, 0);
    const total = subtotal;

    return (
        <section className="cart py-80">
            <div className="container container-lg">
                <div className="row gy-4">
                    <div className="col-xl-9 col-lg-8">
                        <div className="cart-cards">
                            {/* Column labels — shown only from lg up */}
                            <div 
                                className="d-none d-lg-grid align-items-center px-24 py-12 mb-16 rounded-12 bg-gray-50 border border-gray-100"
                                style={{ gridTemplateColumns: 'minmax(260px, 1fr) 120px 150px 120px 50px' }}
                            >
                                <span className="text-xs text-gray-500 fw-bold text-uppercase" style={{ letterSpacing: '0.5px' }}>
                                    Product
                                </span>
                                <span className="text-xs text-gray-500 fw-bold text-uppercase text-center" style={{ letterSpacing: '0.5px' }}>
                                    Price
                                </span>
                                <span className="text-xs text-gray-500 fw-bold text-uppercase text-center" style={{ letterSpacing: '0.5px' }}>
                                    Quantity
                                </span>
                                <span className="text-xs text-gray-500 fw-bold text-uppercase text-center" style={{ letterSpacing: '0.5px' }}>
                                    Subtotal
                                </span>
                                <span className="text-center" />
                            </div>

                            {cartItems.length === 0 ? (
                                <div className="text-center py-48 border border-gray-100 rounded-16 bg-white shadow-xs">
                                    <div className="w-64 h-64 rounded-circle bg-gray-50 border border-gray-100 flex-center mx-auto mb-16 text-gray-400">
                                        <i className="ph ph-shopping-cart-simple text-2xl" />
                                    </div>
                                    <h5 className="mb-8 fw-bold text-gray-900">Your Cart is Empty</h5>
                                    <p className="text-sm text-gray-500 mb-20">Looks like you haven't added any products yet.</p>
                                    <Link to="/shop" className="btn btn-main py-12 px-24 rounded-8 fw-semibold">
                                        Explore Products
                                    </Link>
                                </div>
                            ) : cartItems.map((item) => {
                                const itemPrice = getPrice(item);
                                const maxStock = item.stock !== undefined && item.stock !== null ? Number(item.stock) : 999;
                                const isMaxStockReached = item.quantity >= maxStock;

                                return (
                                    <div
                                        key={item.cartItemId || item.id}
                                        className="cart-item-card border border-gray-100 rounded-16 p-20 p-lg-24 mb-16 bg-white shadow-xs"
                                    >
                                        {/* Desktop Grid Layout (>= lg) */}
                                        <div
                                            className="d-none d-lg-grid align-items-center"
                                            style={{ gridTemplateColumns: 'minmax(260px, 1fr) 120px 150px 120px 50px' }}
                                        >
                                            {/* Product Details */}
                                            <div className="d-flex align-items-center gap-16 pe-16">
                                                <Link
                                                    to={`/product-details/${item.slug || item.id}`}
                                                    className="table-product__thumb border border-gray-100 rounded-12 flex-center flex-shrink-0 bg-gray-50 overflow-hidden"
                                                    style={{ width: '76px', height: '76px' }}
                                                >
                                                    <img 
                                                        src={formatImageUrl(item.image)} 
                                                        alt={item.name || item.title || 'Product'} 
                                                        onError={(e) => {
                                                            e.currentTarget.onerror = null;
                                                            e.currentTarget.src = '/assets/images/thumbs/product-img1.png';
                                                        }}
                                                        className="w-100 h-100 object-fit-contain p-4" 
                                                    />
                                                </Link>
                                                <div className="table-product__content text-start">
                                                    <h6 className="title text-sm fw-bold mb-4">
                                                        <Link to={`/product-details/${item.slug || item.id}`} className="text-gray-900 hover-text-main-600 transition text-line-2">
                                                            {item.name || item.title}
                                                        </Link>
                                                    </h6>
                                                    {item.variantName && (
                                                        <span className="text-xs text-gray-600 bg-gray-50 border border-gray-100 rounded-4 px-8 py-2 d-inline-block">
                                                            {item.variantName}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Unit Price */}
                                            <div className="text-center">
                                                <span className="text-sm fw-semibold text-gray-800">₹{itemPrice.toFixed(2)}</span>
                                            </div>

                                            {/* Quantity Controls */}
                                            <div className="d-flex justify-content-center">
                                                <div 
                                                    className="d-inline-flex align-items-center justify-content-between p-4 rounded-pill border border-gray-200 bg-gray-50 shadow-xs"
                                                    style={{ width: '116px', height: '36px' }}
                                                >
                                                    <button
                                                        type="button"
                                                        disabled={item.quantity <= 1}
                                                        onClick={() => updateCartQuantity(item.cartItemId, item.quantity - 1)}
                                                        className="rounded-circle border-0 flex-center bg-white text-gray-700 hover-bg-main-600 hover-text-white transition shadow-xs"
                                                        style={{ 
                                                            width: '28px', 
                                                            height: '28px', 
                                                            opacity: item.quantity <= 1 ? 0.45 : 1, 
                                                            cursor: item.quantity <= 1 ? 'not-allowed' : 'pointer' 
                                                        }}
                                                        title="Decrease quantity"
                                                    >
                                                        <i className="ph-bold ph-minus text-xs" />
                                                    </button>
                                                    <span className="text-xs fw-bold text-gray-900 text-center select-none" style={{ minWidth: '24px' }}>
                                                        {item.quantity}
                                                    </span>
                                                    <button
                                                        type="button"
                                                        disabled={isMaxStockReached}
                                                        onClick={() => !isMaxStockReached && updateCartQuantity(item.cartItemId, item.quantity + 1)}
                                                        className="rounded-circle border-0 flex-center bg-white text-gray-700 hover-bg-main-600 hover-text-white transition shadow-xs"
                                                        style={{ 
                                                            width: '28px', 
                                                            height: '28px',
                                                            opacity: isMaxStockReached ? 0.45 : 1,
                                                            cursor: isMaxStockReached ? 'not-allowed' : 'pointer'
                                                        }}
                                                        title={isMaxStockReached ? `Maximum available stock (${maxStock}) reached` : "Increase quantity"}
                                                    >
                                                        <i className="ph-bold ph-plus text-xs" />
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Subtotal */}
                                            <div className="text-center">
                                                <span className="text-base fw-bold text-main-600">
                                                    ₹{(itemPrice * item.quantity).toFixed(2)}
                                                </span>
                                            </div>

                                            {/* Remove Button */}
                                            <div className="text-center">
                                                <button
                                                    type="button"
                                                    aria-label="Remove item"
                                                    onClick={() => removeFromCart(item.cartItemId)}
                                                    className="rounded-circle border-0 bg-gray-50 text-gray-400 hover-bg-danger-50 hover-text-danger-600 transition flex-center mx-auto"
                                                    style={{ width: '34px', height: '34px' }}
                                                    title="Remove from cart"
                                                >
                                                    <i className="ph ph-trash text-base" />
                                                </button>
                                            </div>
                                        </div>

                                        {/* Mobile / Tablet View (< lg) */}
                                        <div className="d-lg-none">
                                            <div className="d-flex align-items-start gap-12 mb-16">
                                                <Link
                                                    to={`/product-details/${item.slug || item.id}`}
                                                    className="table-product__thumb border border-gray-100 rounded-12 flex-center flex-shrink-0 bg-gray-50 overflow-hidden"
                                                    style={{ width: '70px', height: '70px' }}
                                                >
                                                    <img 
                                                        src={formatImageUrl(item.image)} 
                                                        alt={item.name || item.title || 'Product'} 
                                                        onError={(e) => {
                                                            e.currentTarget.onerror = null;
                                                            e.currentTarget.src = '/assets/images/thumbs/product-img1.png';
                                                        }}
                                                        className="w-100 h-100 object-fit-contain p-4" 
                                                    />
                                                </Link>
                                                <div className="flex-grow-1 min-w-0">
                                                    <div className="d-flex justify-content-between align-items-start gap-8">
                                                        <h6 className="title text-sm fw-bold mb-4">
                                                            <Link to={`/product-details/${item.slug || item.id}`} className="text-gray-900 hover-text-main-600 transition text-line-2">
                                                                {item.name || item.title}
                                                            </Link>
                                                        </h6>
                                                        <button
                                                            type="button"
                                                            aria-label="Remove item"
                                                            onClick={() => removeFromCart(item.cartItemId)}
                                                            className="rounded-circle border-0 bg-gray-50 text-gray-400 hover-bg-danger-50 hover-text-danger-600 transition flex-center flex-shrink-0"
                                                            style={{ width: '30px', height: '30px' }}
                                                        >
                                                            <i className="ph ph-trash text-sm" />
                                                        </button>
                                                    </div>
                                                    {item.variantName && (
                                                        <span className="text-xs text-gray-600 bg-gray-50 border border-gray-100 rounded-4 px-6 py-2 d-inline-block mb-4">
                                                            {item.variantName}
                                                        </span>
                                                    )}
                                                    <div className="text-xs text-gray-500">
                                                        Unit Price: <span className="fw-semibold text-gray-800">₹{itemPrice.toFixed(2)}</span>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="d-flex justify-content-between align-items-center pt-12 border-top border-gray-100">
                                                {/* Quantity */}
                                                <div 
                                                    className="d-inline-flex align-items-center justify-content-between p-4 rounded-pill border border-gray-200 bg-gray-50 shadow-xs"
                                                    style={{ width: '108px', height: '34px' }}
                                                >
                                                    <button
                                                        type="button"
                                                        disabled={item.quantity <= 1}
                                                        onClick={() => updateCartQuantity(item.cartItemId, item.quantity - 1)}
                                                        className="rounded-circle border-0 flex-center bg-white text-gray-700 hover-bg-main-600 hover-text-white transition shadow-xs"
                                                        style={{ 
                                                            width: '26px', 
                                                            height: '26px', 
                                                            opacity: item.quantity <= 1 ? 0.45 : 1,
                                                            cursor: item.quantity <= 1 ? 'not-allowed' : 'pointer'
                                                        }}
                                                    >
                                                        <i className="ph-bold ph-minus text-xs" />
                                                    </button>
                                                    <span className="text-xs fw-bold text-gray-900 text-center select-none" style={{ minWidth: '20px' }}>
                                                        {item.quantity}
                                                    </span>
                                                    <button
                                                        type="button"
                                                        disabled={isMaxStockReached}
                                                        onClick={() => !isMaxStockReached && updateCartQuantity(item.cartItemId, item.quantity + 1)}
                                                        className="rounded-circle border-0 flex-center bg-white text-gray-700 hover-bg-main-600 hover-text-white transition shadow-xs"
                                                        style={{ 
                                                            width: '26px', 
                                                            height: '26px',
                                                            opacity: isMaxStockReached ? 0.45 : 1,
                                                            cursor: isMaxStockReached ? 'not-allowed' : 'pointer'
                                                        }}
                                                        title={isMaxStockReached ? `Maximum available stock (${maxStock}) reached` : "Increase quantity"}
                                                    >
                                                        <i className="ph-bold ph-plus text-xs" />
                                                    </button>
                                                </div>

                                                {/* Subtotal */}
                                                <div className="text-end">
                                                    <span className="text-xs text-gray-400 d-block">Subtotal</span>
                                                    <span className="text-base fw-bold text-main-600">
                                                        ₹{(itemPrice * item.quantity).toFixed(2)}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    </div>

                    <div className="col-xl-3 col-lg-4">
                        <div className="cart-sidebar border border-gray-100 rounded-16 p-24 bg-white position-sticky shadow-sm" style={{ top: '90px' }}>
                            {/* Card Header */}
                            <div className="d-flex align-items-center justify-content-between pb-16 border-bottom border-gray-100 mb-20">
                                <h6 className="text-lg fw-bold text-gray-900 mb-0 font-heading">Cart Totals</h6>
                                <span className="badge bg-main-50 text-main-600 border border-main-200 rounded-pill px-10 py-4 text-xs fw-semibold">
                                    {cartItems.length} {cartItems.length === 1 ? 'item' : 'items'}
                                </span>
                            </div>

                            {/* Summary Breakdown */}
                            <div className="d-flex flex-column gap-12">
                                <div className="d-flex justify-content-between align-items-center">
                                    <span className="text-gray-600 text-sm fw-medium">Subtotal</span>
                                    <span className="text-gray-900 text-sm fw-bold">₹{subtotal.toFixed(2)}</span>
                                </div>
                    

                                {/* Delivery Charge Notice */}
                                <div className="d-flex align-items-start gap-8 rounded-8 mt-8 mb-4">
                                    <i className="ph-fill ph-truck text-main-600 text-base mt-2 flex-shrink-0" />
                                    <p className="text-xs text-gray-600 mb-0 lh-base">
                                        Delivery charge will be added at the checkout
                                    </p>
                                </div>

                                <div className="border-top border-gray-100 my-4" />

                                {/* Total Amount */}
                                <div className="d-flex justify-content-between align-items-center pt-4">
                                    <div>
                                        <span className="text-gray-900 text-base fw-bold d-block font-heading">Total</span>
                                        <span className="text-xs text-gray-500 fw-normal">(inclu. GST)</span>
                                    </div>
                                    <span className="text-xl fw-bold text-main-600">₹{total.toFixed(2)}</span>
                                </div>
                            </div>

                            {/* Checkout CTA */}
                            {cartItems.length === 0 ? (
                                <button
                                    disabled
                                    className="btn btn-main mt-24 py-14 w-100 rounded-8 fw-semibold d-flex align-items-center justify-content-center gap-8 opacity-50 cursor-not-allowed"
                                >
                                    <span>Proceed to checkout</span>
                                    <i className="ph ph-arrow-right text-base" />
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    onClick={handleProceedToCheckout}
                                    className="btn btn-main mt-24 py-14 w-100 rounded-8 fw-bold d-flex align-items-center justify-content-center gap-8 shadow-sm transition"
                                >
                                    <span>Proceed to checkout</span>
                                    <i className="ph ph-arrow-right text-base" />
                                </button>
                            )}

                            {/* Trust Signals */}
                            <div className="mt-20 pt-16 border-top border-gray-100 d-flex justify-content-center align-items-center gap-16 text-gray-500 text-xs">
                                <span className="d-flex align-items-center gap-4">
                                    <i className="ph-fill ph-shield-check text-main-600 text-sm" /> Secure Checkout
                                </span>
                                <span className="text-gray-300">•</span>
                                <span className="d-flex align-items-center gap-4">
                                    <i className="ph-fill ph-lock-key text-main-600 text-sm" /> Safe Payments
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section >
    )
}

export default CartSection